import { useEffect, type ReactNode } from "react";
import { makeStyles, tokens } from "@fluentui/react-components";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { todayISO } from "@/engine/date";
import { hintBlockedEdit } from "@/features/access/read-only-hint";
import { usePlanSurface } from "@/features/access/use-plan-access";
import { CommandPalette } from "@/features/palette/CommandPalette";
import { CreatePlanDialog } from "@/features/plans/CreatePlanDialog";
import { SettingsDialog } from "@/features/settings/SettingsDialog";
import { SettingsModal } from "@/shell/SettingsHub";
import { useSettingsHub } from "@/shell/settings-hub";
import { StatusUpdateDialog } from "@/features/status/StatusUpdateDialog";
import { ShortcutsDialog } from "@/features/help/ShortcutsDialog";
import { bindNavigate } from "@/app/navigate";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { DesktopRuntime } from "@/desktop/DesktopRuntime";
import { isDesktop } from "@/desktop/runtime";
import { AppNav } from "./AppNav";
import { AppTopBar } from "./AppTopBar";
import { SaveErrorBanner } from "./SaveErrorBanner";
import { RoleDemotionBanner } from "./RoleDemotionBanner";

const useStyles = makeStyles({
  /* 사이드바 바닥과 본문이 맞닿는다. 경계는 선 하나다. */
  root: {
    position: "relative",
    display: "flex",
    flexDirection: "row",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  well: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    boxSizing: "border-box",
    backgroundColor: tokens.colorNeutralBackground2,
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  surface: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  main: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    color: tokens.colorNeutralForeground1,
    fontSize: "15px",
    lineHeight: "22.5px",
    fontWeight: tokens.fontWeightRegular,
  },
});

export function AppShell({ children }: { children?: ReactNode }) {
  const styles = useStyles();
  const navigate = useNavigate();
  const navCollapsed = useCatalogStore((s) => s.navCollapsed);
  useEffect(() => bindNavigate(navigate), [navigate]);
  useAppShortcuts();

  return (
    <div className={styles.root} data-app-shell data-nav-collapsed={navCollapsed ? "" : undefined}>
      <AppNav />
      <div className={styles.well} data-app-well>
        <div className={styles.surface}>
          <AppTopBar />
          <SaveErrorBanner />
          <RoleDemotionBanner />
          <main className={styles.main}>{children ?? <Outlet />}</main>
        </div>
      </div>
      <CommandPalette />
      <CreatePlanDialog />
      <SettingsDialog />
      <SettingsModal />
      <StatusUpdateDialog />
      <ShortcutsDialog />
      <DesktopRuntime />
    </div>
  );
}

/**
 * Global shortcuts. Undo/redo, search and "새 Task" apply to every project tab
 * (schedule, reports, tasks, calendar, docs); Gantt zoom/fit/today stay on the schedule tab.
 */
function useAppShortcuts() {
  const location = useLocation();
  const surface = usePlanSurface();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      const target = e.target as HTMLElement | null;
      const inField = !!target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if (!mod && e.key === "/" && !e.altKey && !inField) {
        e.preventDefault();
        useProjectStore.getState().openDialog("palette");
        return;
      }
      if (!mod) return;
      // 데스크탑 전용: ⌘⌥L 로그, ⌘⌥M MCP 연결(Windows Ctrl+Alt). ⌥가 글자를 바꾸므로 물리 키(code)로 본다.
      if (e.altKey && !e.shiftKey && isDesktop() && (e.code === "KeyL" || e.code === "KeyM")) {
        e.preventDefault();
        useSettingsHub.getState().open("app", e.code === "KeyL" ? "logs" : "integration");
        return;
      }
      const s = useProjectStore.getState();
      const key = e.key.toLowerCase();
      const onProject = /\/(projects|plans)\/[^/]+\/(schedule|reports|status|tasks|calendar|docs)/.test(location.pathname) && !!s.activeId;
      const onSchedule = /\/(projects|plans)\/[^/]+\/schedule/.test(location.pathname);
      const onPlanner = /\/(projects|plans)\/[^/]+\/(tasks|calendar)/.test(location.pathname);

      if (key === "k") {
        e.preventDefault();
        s.openDialog(s.dialog === "palette" ? null : "palette");
      } else if (key === "b" && !inField) {
        e.preventDefault();
        useCatalogStore.getState().setNavCollapsed(!useCatalogStore.getState().navCollapsed);
      } else if (key === ",") {
        e.preventDefault();
        const hub = useSettingsHub.getState();
        const settingsOpen = (hub.scope === "app" || hub.scope === "workspace") && hub.section !== "shortcuts";
        if (settingsOpen) hub.close();
        else hub.open("app");
      } else if (key === "/") {
        e.preventDefault();
        const hub = useSettingsHub.getState();
        if (hub.scope === "app" && hub.section === "shortcuts") hub.close();
        else hub.open("app", "shortcuts");
      } else if (key === "z") {
        if (!onProject || inField) return;
        e.preventDefault();
        if (!surface.undo) return hintBlockedEdit();
        if (e.shiftKey) s.redo();
        else s.undo();
      } else if (key === "e" && !e.shiftKey) {
        // ⌘E: new execution task (any project tab). Opens the detail drawer.
        if (!onProject) return;
        e.preventDefault();
        if (!surface.editTask) return hintBlockedEdit();
        s.addExecutionTask({});
      } else if (key === "i") {
        if (!onSchedule) return;
        e.preventDefault();
        s.setUI({ sidePanel: s.ui.sidePanel ? null : s.focusId ? "inspector" : "insights" });
      } else if (key === "t" && !inField) {
        if (!onSchedule) return;
        e.preventDefault();
        s.scrollToDate(todayISO());
      } else if (["1", "2", "3"].includes(key) && !inField) {
        if (!onSchedule) return;
        e.preventDefault();
        s.setZoomPreset(key === "1" ? "day" : key === "2" ? "week" : "month");
      } else if (key === "0" && !inField) {
        if (!onSchedule) return;
        e.preventDefault();
        s.requestFit(s.selection.length ? "selection" : "project");
      } else if ((key === "=" || key === "+" || key === "-") && !inField) {
        if (!onSchedule) return;
        e.preventDefault();
        s.nudgeZoom(key === "-" ? -1 : 1);
      } else if (key === "backspace" && onPlanner && !inField) {
        // delete the execution task open in the drawer, or the selected rows on the list
        const ids = s.executionSelection.length ? s.executionSelection : s.executionFocusId ? [s.executionFocusId] : [];
        if (!ids.length) return;
        e.preventDefault();
        if (!surface.editTask) return hintBlockedEdit();
        s.deleteExecutionTasks(ids);
      } else if (key === "s") {
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [location.pathname, surface.undo, surface.editTask]);
}
